import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditLikenessScreen = () => {
  const { setCurrentScreen } = useApp();
  const [unitSystem, setUnitSystem] = useState('metric');
  const [formData, setFormData] = useState({
    height: '',
    weight: '',
    body_type: '',
    chest: '',
    waist: '',
    hips: ''
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const bodyTypes = ['Slim', 'Athletic', 'Average', 'Curvy', 'Plus'];

  useEffect(() => {
    const loadLikeness = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        const res = await fetch(`${BACKEND_URL}/api/auth/likeness`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          if (result.data) {
            setFormData({
              height: result.data.height ? String(result.data.height) : '',
              weight: result.data.weight ? String(result.data.weight) : '',
              body_type: result.data.body_type || '',
              chest: result.data.chest ? String(result.data.chest) : '',
              waist: result.data.waist ? String(result.data.waist) : '',
              hips: result.data.hips ? String(result.data.hips) : '',
            });
            setUnitSystem(result.data.unit_system || 'metric');
          }
        }
      } catch (e) {
        console.error('Error loading likeness:', e);
      } finally {
        setLoaded(true);
      }
    };
    loadLikeness();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/auth/likeness`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          height: parseFloat(formData.height) || null,
          weight: parseFloat(formData.weight) || null,
          body_type: formData.body_type || null,
          chest: parseFloat(formData.chest) || null,
          waist: parseFloat(formData.waist) || null,
          hips: parseFloat(formData.hips) || null,
          unit_system: unitSystem
        })
      });

      if (response.ok) {
        setCurrentScreen('profile');
      }
    } catch (error) {
      console.error('Error saving likeness:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-4">
        <button
          onClick={() => setCurrentScreen('profile')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <img src="/logos/logo-black.jpg" alt="ATELIA" className="h-24 w-auto object-contain" />
      </div>

      <div className="px-6 pb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-2 font-bold">AVATAR SETUP</p>
        <h1 className="text-2xl font-bold text-black uppercase tracking-wide">Edit Likeness</h1>
      </div>

      {/* Unit System Toggle */}
      <div className="px-6 mb-6">
        <div className="flex gap-2 p-1 bg-black/5 rounded-xl w-fit">
          <button
            onClick={() => setUnitSystem('metric')}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all ${
              unitSystem === 'metric' ? 'bg-black text-white' : 'text-black/55'
            }`}
          >
            Metric
          </button>
          <button
            onClick={() => setUnitSystem('imperial')}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all ${
              unitSystem === 'imperial' ? 'bg-black text-white' : 'text-black/55'
            }`}
          >
            Imperial
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="px-6 space-y-4">
        {/* Height */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-2 block font-bold">
            Height ({unitSystem === 'metric' ? 'cm' : 'ft/in'})
          </label>
          <input
            type="number"
            value={formData.height}
            onChange={(e) => setFormData({...formData, height: e.target.value})}
            placeholder={unitSystem === 'metric' ? '170' : '5.7'}
            className="w-full px-4 py-3 bg-white border border-black/12 rounded-xl
              focus:outline-none focus:border-black/30 transition-all"
          />
        </div>

        {/* Weight */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-2 block font-bold">
            Weight ({unitSystem === 'metric' ? 'kg' : 'lbs'})
          </label>
          <input
            type="number"
            value={formData.weight}
            onChange={(e) => setFormData({...formData, weight: e.target.value})}
            placeholder={unitSystem === 'metric' ? '65' : '143'}
            className="w-full px-4 py-3 bg-white border border-black/12 rounded-xl
              focus:outline-none focus:border-black/30 transition-all"
          />
        </div>

        {/* Body Type */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-2 block font-bold">
            Body Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {bodyTypes.map((type) => (
              <button
                key={type}
                onClick={() => setFormData({...formData, body_type: type})}
                className={`py-3 rounded-xl text-sm font-bold uppercase transition-all ${
                  formData.body_type === type
                    ? 'bg-black text-white'
                    : 'bg-white border border-black/12 text-black hover:bg-black/5'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Measurements (Optional) */}
        <div>
          <label className="text-xs uppercase tracking-wide text-black/55 mb-2 block font-bold">
            Measurements (Optional)
          </label>
          <div className="grid grid-cols-3 gap-2">
            <input
              type="number"
              value={formData.chest}
              onChange={(e) => setFormData({...formData, chest: e.target.value})}
              placeholder="Chest"
              className="px-3 py-2 bg-white border border-black/12 rounded-xl text-sm
                focus:outline-none focus:border-black/30"
            />
            <input
              type="number"
              value={formData.waist}
              onChange={(e) => setFormData({...formData, waist: e.target.value})}
              placeholder="Waist"
              className="px-3 py-2 bg-white border border-black/12 rounded-xl text-sm
                focus:outline-none focus:border-black/30"
            />
            <input
              type="number"
              value={formData.hips}
              onChange={(e) => setFormData({...formData, hips: e.target.value})}
              placeholder="Hips"
              className="px-3 py-2 bg-white border border-black/12 rounded-xl text-sm
                focus:outline-none focus:border-black/30"
            />
          </div>
          <p className="text-xs text-black/55 mt-2">in {unitSystem === 'metric' ? 'cm' : 'inches'}</p>
        </div>
      </div>

      {/* Save Button */}
      <div className="px-6 mt-8">
        <button
          onClick={handleSave}
          disabled={saving || !formData.height || !formData.weight || !formData.body_type}
          className="w-full bg-black text-white py-4 rounded-xl font-bold uppercase
            flex items-center justify-center gap-2 hover:bg-black/90 transition-all
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Likeness'}
        </button>
      </div>
    </div>
  );
};

export default EditLikenessScreen;